import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import Store from '@ember-data/store';
import SelectSuppliers from 'bcl/components/input/select-suppliers';
import PageSetup from 'bcl/components/page-setup';
import type SupplierModel from 'bcl/models/supplier';
import { pageTitle } from 'ember-page-title';
import type SupplierItemModel from 'bcl/models/supplier-item';
import { Input } from '@ember/component';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import type NotificationService from 'bcl/services/notification';
import euro, { reverseEuro } from 'bcl/helpers/euro';
import InputWithFormatting from 'bcl/components/input/with-formatting.gts';

interface SuppliersSignature {
  Args: {
    model: unknown;
    controller: unknown;
  };
}
export default class ViewsSuppliersRoute extends Component<SuppliersSignature> {
  @service declare store: Store;
  @service declare notification: NotificationService;

  @tracked showAll = false;

  get items() {
    const all = this.store.peekAll<SupplierItemModel>('supplier-item');
    const filtered = all
      .filter((i) => i.supplier === this.supplier && !i.isDeleted)
      .filter((i) => this.showAll || i.standard_order);
    return filtered.sort((a, b) => a.item.name.localeCompare(b.item.name));
  }
  @action toggleShowAll() {
    this.showAll = !this.showAll;
  }
  @tracked selectedSupplier?: SupplierModel;

  get supplier() {
    return (
      this.selectedSupplier ??
      this.store
        .peekAll<SupplierModel>('supplier')
        .filter((supplier) => supplier.active)
        .sort((a, b) => a.name.localeCompare(b.name))[0]
    );
  }

  @action changeSupplier(supplier: { id: string; name: string }) {
    this.selectedSupplier =
      this.store.peekRecord<SupplierModel>('supplier', supplier.id) ??
      undefined;
  }
  @action async saveFor(suplItem: SupplierItemModel) {
    await suplItem.save();
    this.notification.success('Erfolgreich gespeichert!');
  }
  <template>
    {{pageTitle "Bestellmengen"}}
    <PageSetup @title="Bestellmengen">

      {{#if this.supplier}}
        <SelectSuppliers
          @label="Lieferant"
          @name="supplier"
          @onChange={{this.changeSupplier}}
          @value={{this.supplier}}
        />
      {{/if}}
      <div class="mt-8 flow-root">
        <button
          {{on "click" this.toggleShowAll}}
          type="button"
          class="inline-block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >{{#if this.showAll}}Nicht mehr alle{{else}}Alle{{/if}}
          anzeigen</button>
        <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div class="inline-block min-w-full py-2 align-middle">
            <table
              class="relative min-w-full divide-y divide-gray-300 dark:divide-white/15"
            >
              <thead>
                <tr>
                  <th
                    scope="col"
                    class="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-6 lg:pl-8 dark:text-white"
                  >Name</th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white"
                  >Einkaufspreis</th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white"
                  >Standardbestellung</th>
                  <th scope="col" class="py-3.5 pr-4 pl-3 sm:pr-6 lg:pr-8">
                    <span class="sr-only">Speichern</span>
                  </th>
                </tr>
              </thead>
              <tbody
                class="divide-y divide-gray-200 bg-white dark:divide-white/10 dark:bg-gray-900"
              >
                {{#each this.items as |suplItem|}}
                  <tr>
                    <td
                      class="py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap text-gray-900 sm:pl-6 lg:pl-8 dark:text-white"
                    >{{suplItem.item.name}}</td>
                    <td
                      class="px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400"
                    >
                      <InputWithFormatting
                        @label="Preis"
                        @name="price"
                        @type="text"
                        @value={{euro suplItem.price}}
                        @update={{fn (mut suplItem.price)}}
                        @updateMut={{reverseEuro}}
                      />
                    </td>
                    <td
                      class="px-3 py-4 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400"
                    >
                      <Input
                        @type="number"
                        @value={{suplItem.standard_order}}
                        aria-label="Standardbestellung für {{suplItem.item.name}}"
                      />
                    </td>

                    <td
                      class="py-4 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap sm:pr-6 lg:pr-8"
                    >
                      <button
                        {{on "click" (fn this.saveFor suplItem)}}
                        type="button"
                        disabled={{or
                          (not suplItem.hasDirtyAttributes)
                          suplItem.isSaving
                        }}
                        class="text-indigo-600 enabled:hover:text-indigo-900 disabled:cursor-not-allowed disabled:text-gray-400 dark:text-indigo-400 dark:enabled:hover:text-indigo-300 dark:disabled:text-gray-600"
                      >speichern</button>
                    </td>
                  </tr>
                {{else}}
                  <tr>
                    <td
                      colspan="4"
                      class="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      {{#if this.supplier}}
                        {{#if this.showAll}}
                          Für diesen Lieferanten sind keine Artikel vorhanden.
                        {{else}}
                          Keine Artikel mit Bestellmenge vorhanden. Über „Alle
                          anzeigen“ werden auch Artikel ohne Bestellmenge
                          angezeigt.
                        {{/if}}
                      {{else}}
                        Keine aktiven Lieferanten vorhanden.
                      {{/if}}
                    </td>
                  </tr>
                {{/each}}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PageSetup>
  </template>
}
